/**
 * Cognito Authentication Service
 *
 * This service provides authentication functionality using AWS Amplify and Cognito.
 * It handles user sign-in, sign-out, session management, and token refresh.
 *
 * The service must be configured with Cognito settings before use via configureAuth().
 *
 * @module services/auth
 */

import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { User, UserRole } from '../types/user';

/**
 * Configuration interface for Cognito authentication settings.
 * These values are typically provided via environment variables.
 */
export interface CognitoConfig {
  /** The AWS region where the Cognito User Pool is located */
  region: string;
  /** The Cognito User Pool ID */
  userPoolId: string;
  /** The Cognito User Pool Client ID */
  userPoolClientId: string;
  /** Optional: The domain for hosted UI (if using) */
  domain?: string;
  /** Optional: Redirect URLs for OAuth flows */
  redirectSignIn?: string;
  redirectSignOut?: string;
}

/**
 * Result of a successful sign-in operation.
 */
export interface SignInResult {
  /** Whether the sign-in was successful */
  isSignedIn: boolean;
  /** The authenticated user information */
  user: User | null;
  /** Any additional sign-in steps required (e.g., MFA) */
  nextStep?: {
    signInStep: string;
    [key: string]: unknown;
  };
}

/**
 * Result of getting the current user.
 */
export interface CurrentUserResult {
  /** Whether a user is currently authenticated */
  isAuthenticated: boolean;
  /** The current user information, or null if not authenticated */
  user: User | null;
}

/**
 * Authentication error types for better error handling.
 */
export type AuthErrorCode =
  | 'NotAuthorizedException'
  | 'UserNotFoundException'
  | 'UserNotConfirmedException'
  | 'PasswordResetRequiredException'
  | 'InvalidParameterException'
  | 'NetworkError'
  | 'UnknownError';

/**
 * Custom error class for authentication errors.
 */
export class AuthError extends Error {
  code: AuthErrorCode;
  originalError?: Error;

  constructor(message: string, code: AuthErrorCode, originalError?: Error) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Flag to track if Amplify has been configured.
 */
let isConfigured = false;

/**
 * Configure AWS Amplify with Cognito settings.
 *
 * This function must be called before using any other auth functions.
 * Typically called once at application startup.
 *
 * @param config - The Cognito configuration settings
 * @throws AuthError if configuration fails
 *
 * @example
 * ```typescript
 * configureAuth({
 *   region: 'us-east-1',
 *   userPoolId: 'us-east-1_xxxxx',
 *   userPoolClientId: 'xxxxxxxxxxxxxxxxxxxxxxxxxx',
 * });
 * ```
 */
export function configureAuth(config: CognitoConfig): void {
  try {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.userPoolId,
          userPoolClientId: config.userPoolClientId,
          loginWith: config.domain
            ? {
                oauth: {
                  domain: config.domain,
                  scopes: ['openid', 'email', 'profile'],
                  redirectSignIn: config.redirectSignIn ? [config.redirectSignIn] : [],
                  redirectSignOut: config.redirectSignOut ? [config.redirectSignOut] : [],
                  responseType: 'code',
                },
              }
            : undefined,
        },
      },
    });
    isConfigured = true;
  } catch (error) {
    throw new AuthError(
      'Failed to configure authentication',
      'UnknownError',
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Check if the auth service has been configured.
 *
 * @returns true if configureAuth has been called successfully
 */
export function isAuthConfigured(): boolean {
  return isConfigured;
}

/**
 * Sign in a user with email and password.
 *
 * @param email - The user's email address
 * @param password - The user's password
 * @returns Promise resolving to the sign-in result
 * @throws AuthError if sign-in fails
 *
 * @example
 * ```typescript
 * try {
 *   const result = await signIn('user@example.com', 'password123');
 *   if (result.isSignedIn) {
 *     console.log('Signed in as:', result.user?.email);
 *   }
 * } catch (error) {
 *   if (error instanceof AuthError) {
 *     console.error('Auth error:', error.code, error.message);
 *   }
 * }
 * ```
 */
export async function signIn(email: string, password: string): Promise<SignInResult> {
  try {
    const result = await amplifySignIn({
      username: email,
      password,
    });

    if (result.isSignedIn) {
      const user = await buildUserFromSession();
      return {
        isSignedIn: true,
        user,
      };
    }

    // Handle additional sign-in steps (MFA, new password required, etc.)
    return {
      isSignedIn: false,
      user: null,
      nextStep: {
        ...result.nextStep,
      },
    };
  } catch (error) {
    throw mapCognitoError(error);
  }
}

/**
 * Sign out the current user.
 *
 * @param global - If true, signs out from all devices (global sign-out)
 * @throws AuthError if sign-out fails
 *
 * @example
 * ```typescript
 * await signOut();
 * // User is now signed out
 * ```
 */
export async function signOut(global: boolean = false): Promise<void> {
  try {
    await amplifySignOut({ global });
  } catch (error) {
    throw mapCognitoError(error);
  }
}

/**
 * Get the currently authenticated user.
 *
 * @returns Promise resolving to the current user result
 *
 * @example
 * ```typescript
 * const { isAuthenticated, user } = await getCurrentUser();
 * if (isAuthenticated) {
 *   console.log('Current user:', user?.email);
 * }
 * ```
 */
export async function getCurrentUser(): Promise<CurrentUserResult> {
  try {
    const authUser = await amplifyGetCurrentUser();
    if (authUser) {
      const user = await buildUserFromSession();
      return {
        isAuthenticated: true,
        user,
      };
    }
    return {
      isAuthenticated: false,
      user: null,
    };
  } catch {
    // User is not authenticated
    return {
      isAuthenticated: false,
      user: null,
    };
  }
}

/**
 * Get the JWT ID token for the current session.
 *
 * This token can be used for authenticating API requests.
 *
 * @returns Promise resolving to the ID token string, or null if not authenticated
 *
 * @example
 * ```typescript
 * const token = await getIdToken();
 * if (token) {
 *   // Use token for API calls
 *   fetch('/api/data', {
 *     headers: { Authorization: `Bearer ${token}` }
 *   });
 * }
 * ```
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Get the JWT access token for the current session.
 *
 * @returns Promise resolving to the access token string, or null if not authenticated
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Refresh the current authentication session.
 *
 * This function forces a refresh of the authentication tokens.
 * Amplify typically handles token refresh automatically, but this
 * can be called explicitly when needed.
 *
 * @returns Promise resolving to true if refresh was successful
 * @throws AuthError if refresh fails
 *
 * @example
 * ```typescript
 * try {
 *   const success = await refreshSession();
 *   if (success) {
 *     console.log('Session refreshed successfully');
 *   }
 * } catch (error) {
 *   // Session could not be refreshed, user needs to sign in again
 *   redirectToLogin();
 * }
 * ```
 */
export async function refreshSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens !== undefined;
  } catch (error) {
    throw mapCognitoError(error);
  }
}

/**
 * Check if the current session is valid and not expired.
 *
 * @returns Promise resolving to true if the session is valid
 */
export async function isSessionValid(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      return false;
    }
    // Check if token is expired
    const expiration = session.tokens.idToken.payload.exp;
    if (typeof expiration === 'number') {
      return Date.now() < expiration * 1000;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Build a User object from the current Cognito session.
 *
 * @returns Promise resolving to a User object
 */
async function buildUserFromSession(): Promise<User | null> {
  try {
    const authUser = await amplifyGetCurrentUser();
    const session = await fetchAuthSession();

    if (!authUser || !session.tokens?.idToken) {
      return null;
    }

    const idToken = session.tokens.idToken;
    const payload = idToken.payload;

    // Extract user attributes from token claims
    const email = (payload.email as string) || authUser.username;
    const name = (payload.name as string) || (payload['cognito:username'] as string) || email.split('@')[0];
    const sub = payload.sub as string;

    // Determine role from Cognito groups or custom attribute
    const groups = (payload['cognito:groups'] as string[]) || [];
    const customRole = payload['custom:role'] as string | undefined;
    const role: UserRole = groups.includes('admin') || customRole === 'admin' ? 'admin' : 'user';

    // Get timestamps from token
    const authTime = payload.auth_time as number | undefined;
    const iat = payload.iat as number | undefined;

    return {
      id: sub,
      email,
      name,
      role,
      enabled: true, // Cognito users are enabled if they can authenticate
      createdAt: iat ? new Date(iat * 1000).toISOString() : new Date().toISOString(),
      lastLogin: authTime ? new Date(authTime * 1000).toISOString() : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Map Cognito errors to AuthError with appropriate error codes.
 *
 * @param error - The error from Cognito/Amplify
 * @returns An AuthError with the appropriate code
 */
function mapCognitoError(error: unknown): AuthError {
  if (error instanceof AuthError) {
    return error;
  }

  const err = error as { name?: string; message?: string };
  const errorName = err.name || '';
  const errorMessage = err.message || 'An unknown error occurred';

  switch (errorName) {
    case 'NotAuthorizedException':
      return new AuthError(
        'Incorrect email or password',
        'NotAuthorizedException',
        error instanceof Error ? error : undefined
      );

    case 'UserNotFoundException':
      return new AuthError(
        'User not found',
        'UserNotFoundException',
        error instanceof Error ? error : undefined
      );

    case 'UserNotConfirmedException':
      return new AuthError(
        'User account is not confirmed. Please check your email for verification.',
        'UserNotConfirmedException',
        error instanceof Error ? error : undefined
      );

    case 'PasswordResetRequiredException':
      return new AuthError(
        'Password reset is required',
        'PasswordResetRequiredException',
        error instanceof Error ? error : undefined
      );

    case 'InvalidParameterException':
      return new AuthError(
        'Invalid parameters provided',
        'InvalidParameterException',
        error instanceof Error ? error : undefined
      );

    case 'NetworkError':
      return new AuthError(
        'Network error. Please check your connection.',
        'NetworkError',
        error instanceof Error ? error : undefined
      );

    default:
      return new AuthError(
        errorMessage,
        'UnknownError',
        error instanceof Error ? error : undefined
      );
  }
}

/**
 * Auth service object for convenient access to all auth functions.
 */
export const authService = {
  configureAuth,
  isAuthConfigured,
  signIn,
  signOut,
  getCurrentUser,
  getIdToken,
  getAccessToken,
  refreshSession,
  isSessionValid,
};

export default authService;
